import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { AxiosError } from "axios";
import apiService from "../../../../service/Api";

const API_BASE_URL =
  window.location.hostname === "localhost" ? "http://localhost:8000" : "";

interface CustomUser {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  profile_picture: string | null;
  profile_picture_url: string | null;
  profile_picture_base64: string | null;
  last_login: string | null; // This represents the specific login instance timestamp
  is_active: boolean;
}

interface UserLoginResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustomUser[];
}

const getAvatarColor = (name: string) => {
  if (!name) return "#1976D2";
  const colors = [
    "#1976D2",
    "#388E3C",
    "#F57C00",
    "#7B1FA2",
    "#C62828",
    "#00796B",
    "#5D4037",
    "#455A64",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export default function UserLoginMonitor() {
  const [logs, setLogs] = useState<CustomUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const fetchLoginMonitor = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * rowsPerPage;

      // We pass both standard pagination styles simultaneously to make sure your specific backend layout registers it
      let url = `/api/authentication/users/last-login/?page=${page}&page_size=${rowsPerPage}&limit=${rowsPerPage}&offset=${offset}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const response = await apiService.get<UserLoginResponse>(url);

      // Map results safely
      setLogs(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (e) {
      if (e instanceof AxiosError) {
        const msg =
          e.response?.data?.detail ||
          e.message ||
          "Failed to load login history logs.";
        setError(msg);
      } else {
        setError("An unexpected error occurred.");
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    fetchLoginMonitor();
  }, [fetchLoginMonitor]);

  const getUserFullName = (user: CustomUser) => {
    return (
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      user.username ||
      "System User"
    );
  };

  // Displays both date and hour clearly for auditing chronologically
  const formatLoginTime = (dateString: string | null) => {
    if (!dateString) return "Unknown Time";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    return (
      d.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      " at " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  // Inspects asset records dynamically across all variations
  const resolveAvatarSource = (user: CustomUser) => {
    if (user.profile_picture_base64) {
      return user.profile_picture_base64.startsWith("data:image")
        ? user.profile_picture_base64
        : `data:image/jpeg;base64,${user.profile_picture_base64}`;
    }

    const targetPath = user.profile_picture_url || user.profile_picture;
    if (!targetPath) return undefined;

    if (targetPath.startsWith("http") || targetPath.startsWith("data:")) {
      return targetPath;
    }

    return `${API_BASE_URL}${targetPath.startsWith("/") ? "" : "/"}${targetPath}`;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F5F5", p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid #E0E0E0",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: "1px solid #E0E0E0" }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#1A1A1A", mb: 2 }}
          >
            User Login History Monitor
          </Typography>

          {/* Controls */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              placeholder="Search user or email..."
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#FAFAFA",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9E9E9E", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchLoginMonitor}
              disabled={loading}
              sx={{
                borderRadius: 2,
                bgcolor: "#1A1A1A",
                textTransform: "none",
                "&:hover": { bgcolor: "#333333" },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Table Content */}
        <TableContainer sx={{ position: "relative", minHeight: "200px" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.7)",
                zIndex: 2,
              }}
            >
              <CircularProgress color="inherit" size={30} />
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FAFAFA" }}>
                <TableCell
                  sx={{ fontWeight: 600, color: "#616161", fontSize: 13 }}
                >
                  User
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#616161", fontSize: 13 }}
                >
                  Role
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#616161", fontSize: 13 }}
                >
                  Login Event Timestamp
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loading ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 4, color: "#9E9E9E" }}
                  >
                    No login events tracked.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((user, idx) => {
                  const fullName = getUserFullName(user);
                  const avatarSrc = resolveAvatarSource(user);

                  // Generates a bulletproof unique item key compound even if user records repeat
                  const itemKey = `${user.id}-${user.last_login}-${idx}`;

                  return (
                    <TableRow
                      key={itemKey}
                      sx={{
                        "&:hover": { bgcolor: "#FAFAFA" },
                        "& td": { borderColor: "#F0F0F0" },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={avatarSrc}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: getAvatarColor(fullName),
                              fontSize: 16,
                            }}
                          >
                            {fullName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </Avatar>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: 14,
                                color: "#1A1A1A",
                              }}
                            >
                              {fullName}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#9E9E9E" }}>
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 14, color: "#616161" }}>
                          {user.role || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#2E7D32",
                          }}
                        >
                          {formatLoginTime(user.last_login)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
            borderTop: "1px solid #E0E0E0",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#616161", marginRight: 1 }}>
            Page {page} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              "&:disabled": { opacity: 0.5 },
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.ceil(totalCount / rowsPerPage) || loading}
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: 1,
              "&:disabled": { opacity: 0.5 },
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
